import { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  Building2, Users, Headphones, CreditCard, TrendingUp,
  AlertCircle, ArrowRight, FileText, Layout
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeDashboard() {
  const navigate = useNavigate();
  const { user, theme } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const isDark = theme === 'dark';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch multiple endpoints in parallel
      const [statsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/super-admin/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/support/admin/tickets?status=active`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);

      if (statsRes.data) {
        setStats(statsRes.data);
      }

      if (ticketsRes.data) {
        setTicketStats(ticketsRes.data.stats);
        setRecentTickets(ticketsRes.data.tickets.slice(0, 5));
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      open: isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700",
      in_progress: isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700",
      waiting_customer: isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-700",
      requested_info: isDark ? "bg-orange-500/20 text-orange-400" : "bg-orange-100 text-orange-700",
      resolved: isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700",
      closed: isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-600"
    };
    return colors[status] || (isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-600");
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: isDark ? "text-red-400" : "text-red-600",
      high: isDark ? "text-orange-400" : "text-orange-600",
      medium: isDark ? "text-amber-400" : "text-amber-600",
      low: isDark ? "text-green-400" : "text-green-600"
    };
    return colors[priority] || (isDark ? "text-slate-400" : "text-gray-600");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Welcome back, {user?.name?.split(' ')[0] || 'Admin'}</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Here&apos;s what&apos;s happening today</p>
        </div>
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          {new Date().toLocaleDateString('en-GB', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card 
          className={`p-5 cursor-pointer transition-all hover:border-blue-500/50 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
          onClick={() => navigate('/backoffice/customers')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Customers</p>
              <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats?.total_companies || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-500">+{stats?.new_companies_this_month || 0}</span>
            <span className={`ml-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>this month</span>
          </div>
        </Card>

        {/* Total Layouts — sourced from /super-admin/stats.total_layouts which
            counts every doc in the layouts collection across all companies. */}
        <Card
          className={`p-5 cursor-pointer transition-all hover:border-violet-500/50 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
          onClick={() => navigate('/backoffice/customers')}
          data-testid="bo-stat-layouts"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Layouts</p>
              <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{(stats?.total_layouts || 0).toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <Layout className="w-6 h-6 text-violet-500" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Across all customers</span>
          </div>
        </Card>

        <Card 
          className={`p-5 cursor-pointer transition-all hover:border-green-500/50 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
          onClick={() => navigate('/backoffice/subscriptions')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Active Subscriptions</p>
              <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats?.active_subscriptions || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-green-500" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>MRR:</span>
            <span className={`font-medium ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(stats?.mrr || 0)}</span>
          </div>
        </Card>

        <Card 
          className={`p-5 cursor-pointer transition-all hover:border-amber-500/50 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
          onClick={() => navigate('/backoffice/support')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Open Tickets</p>
              <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticketStats?.active || 0}</p>
            </div>
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Headphones className="w-6 h-6 text-amber-500" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            {ticketStats?.urgent > 0 && (
              <>
                <AlertCircle className="w-4 h-4 text-red-500 mr-1" />
                <span className="text-red-500">{ticketStats.urgent} urgent</span>
              </>
            )}
            {ticketStats?.unassigned > 0 && (
              <span className={`ml-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{ticketStats.unassigned} unassigned</span>
            )}
          </div>
        </Card>

        <Card 
          className={`p-5 cursor-pointer transition-all hover:border-violet-500/50 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
          onClick={() => navigate('/backoffice/users')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Internal Users</p>
              <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats?.total_internal_users || 0}</p>
            </div>
            <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-violet-500" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Customers:</span>
            <span className={`font-medium ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats?.total_customers || 0}</span>
          </div>
        </Card>
      </div>

      {/* Subscription lifecycle scan status — surfaces the hourly cron's
          last-run timestamp + counters and exposes a manual re-run button
          for super admins. Helps confirm the auto-expire job is healthy. */}
      <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} data-testid="bo-lifecycle-scan-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Subscription lifecycle scan</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-testid="bo-lifecycle-scan-meta">
                {stats?.lifecycle_last_scan?.ran_at
                  ? `Last run: ${new Date(stats.lifecycle_last_scan.ran_at).toLocaleString()} • flipped ${stats.lifecycle_last_scan.flipped || 0}, leads seeded ${stats.lifecycle_last_scan.leads_seeded || 0}`
                  : 'Auto-runs hourly. No scan recorded yet.'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            data-testid="bo-lifecycle-scan-run"
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/subscriptions/super-admin/lifecycle/scan`, {}, { withCredentials: true });
                const flipped = r.data?.flipped ?? 0;
                const leads = r.data?.leads_seeded ?? 0;
                window.alert(`Lifecycle scan complete — flipped ${flipped} subscription(s), seeded ${leads} win-back lead(s).`);
                fetchDashboardData();
              } catch (e) {
                window.alert('Scan failed. Check backend logs.');
              }
            }}
          >
            Run scan now
          </Button>
        </div>
      </Card>

      {/* Ticket Overview & Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Support Ticket Summary */}
        <Card className={`lg:col-span-2 p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Overview</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/backoffice/support')}
              className="text-violet-600 hover:text-violet-700"
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Ticket Status Breakdown */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Open</p>
              <p className={`text-xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{ticketStats?.open || 0}</p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>In Progress</p>
              <p className={`text-xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{ticketStats?.in_progress || 0}</p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Waiting Customer</p>
              <p className={`text-xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{ticketStats?.waiting_customer || 0}</p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Resolved Today</p>
              <p className={`text-xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{ticketStats?.resolved || 0}</p>
            </div>
          </div>

          {/* Recent Tickets */}
          <div className="space-y-3">
            <h3 className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Recent Tickets</h3>
            {recentTickets.length === 0 ? (
              <p className={`text-center py-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>No active tickets</p>
            ) : (
              recentTickets.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  onClick={() => navigate(`/backoffice/support?ticket=${ticket.ticket_id}`)}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-gray-50 hover:bg-gray-100'}`}
                >
                  <div className={`w-2 h-2 rounded-full ${getPriorityColor(ticket.priority).replace('text-', 'bg-')}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{ticket.ticket_number} • {ticket.user_email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ticket.status)}`}>
                    {ticket.status.replace('_', ' ')}
                  </span>
                  {ticket.assigned_to_name ? (
                    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{ticket.assigned_to_name}</span>
                  ) : (
                    <span className="text-xs text-orange-500">Unassigned</span>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Quick Actions */}
        <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Quick Actions</h2>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/backoffice/customers')}
              className={`w-full justify-start ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
            >
              <Building2 className="w-4 h-4 mr-2" />
              View All Customers
            </Button>
            <Button
              onClick={() => navigate('/backoffice/support')}
              className={`w-full justify-start ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
            >
              <Headphones className="w-4 h-4 mr-2" />
              Manage Tickets
            </Button>
            <Button
              onClick={() => navigate('/backoffice/invoices')}
              className={`w-full justify-start ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
            >
              <FileText className="w-4 h-4 mr-2" />
              View Invoices
            </Button>
            <Button
              onClick={() => navigate('/backoffice/users')}
              className={`w-full justify-start ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
            >
              <Users className="w-4 h-4 mr-2" />
              Manage Users
            </Button>
            <Button
              onClick={() => navigate('/backoffice/settings')}
              className="w-full justify-start bg-violet-600 hover:bg-violet-700 text-white"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              Platform Settings
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
